import postal from 'postal';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

import AppV1 from '@/components/App';
import { useFlags } from '@/featureFlags/useFlags';
import { KindOf } from '@/utilities/HasKind';
import { useOnlineStatus } from '@/utilities/hooks/useOnlineStatus';

import { RxDBHandler } from '../rxdb/useRxDB';
import {
  FeatureFlagsChanged,
  kind as featureFlagsChanged,
} from './replication-v2/actions/FeatureFlagsChanged';
import { createGoal } from './replication-v2/actions/goals/Goal';
import { Goals } from './replication-v2/actions/goals/Goals';
import { kind as networkChanged, NetworkChanged } from './replication-v2/actions/NetworkChanged';
import { App as AppV2 } from './replication-v2/App/App';
import {
  dataManagerContext,
  dummyDispatch,
  initialState,
} from './replication-v2/dataManagerContext';
import { LegacyRxdbAdapter } from './replication-v2/legacy/LegacyRxdbAdapter';
import { LegacyRxdbManagerHandler } from './replication-v2/legacy/LegacyRxdbManagerHandler';
import { useBorerSelection } from './replication-v2/useBorerSelection';
import { useTrackEvents } from './replication-v2/useTrackEvents';
import RxdbManager from './RxdbManager';

export const ReplicationChooser: FC = () => {
  // The application code expects & requires a stable IRxdbInstance to work correctly.
  const adapter = useMemo(() => {
    // Since we need the instance to be stable,
    // we need to create it independently of DataManager updates.
    // That means that we need to default to `initialState` and `dummyDispatch`.
    // However, these should not last long; the next effect updates them to their managed values.
    return new LegacyRxdbAdapter(initialState, dummyDispatch);
  }, []);

  const { state, dispatch } = useContext(dataManagerContext);

  useEffect(() => adapter.update(state, dispatch), [state, dispatch, adapter]);

  useEffect(() => {
    // Back door into controlling the sync state from the console.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).replicationGoal = (kind: KindOf<Goals>) => {
      const goal = createGoal(kind);
      dispatch(goal);
      return goal;
    };
  }, [dispatch]);

  const featureFlags = useFlags();
  useEffect(() => {
    const action: FeatureFlagsChanged = {
      kind: featureFlagsChanged,
      featureFlags: {
        ...featureFlags,
        // TODO: This is a temp update for main branch v1 replication as we should never be turning
        // on the syncEnhancements flag for legacy replication. We should remove this once we have v2 replication in main.
        // Making a note so it is not missed during merge replication-v2 to main.
        syncEnhancements: false,
      },
    };
    dispatch(action);
  }, [dispatch, featureFlags]);

  const online = useOnlineStatus();
  useEffect(() => {
    const action: NetworkChanged = {
      kind: networkChanged,
      online,
    };
    dispatch(action);
  }, [dispatch, online]);

  useEffect(() => {
    const message = state.legacy ? 'Using Legacy Replication' : 'Using Replication V2';
    // Log this so that we can easily see which system we're using.
    // eslint-disable-next-line no-console
    console.debug(message);
  }, [state.legacy]);

  useTrackEvents();
  useBorerSelection();

  const [previousLegacy, setPreviousLegacy] = useState<boolean>(state.legacy);
  useEffect(() => {
    if (!previousLegacy && state.legacy) {
      // `RxDBHandler` only begins replication once it's mounted _and_ `db.initialized` and `borer.selected` are published.
      // This combination of `previousLegacy` and `state.legacy` only occurs after the downgrade completes.
      // Because we're in a `useEffect`, this will be called after `RxDBHandler` has been mounted,
      // giving us the opportunity to publish the necessary messags.
      // eslint-disable-next-line no-console
      console.debug('Triggering Legacy Replication');

      // This is what `RxDBManager` does after it sets its database.
      if (RxdbManager.instance.isInitialized) {
        postal.publish({
          channel: 'db',
          topic: 'db.initialized',
        });
      }

      if (state.borerSelected) {
        postal.publish({
          channel: 'borer',
          topic: 'borer.selected',
        });
      }
    }
    setPreviousLegacy(state.legacy);
  }, [previousLegacy, state.legacy, state.borerSelected]);

  // WARNING: Setting `RxdbManager.instance` to `RxdbManager.legacyInstance`
  // must happen *before* `RxDBHandler` is mounted.
  // If `RxDBHandler` is ever mounted with `adapter`
  // then something (unknown) triggers an infinite render loop in `ReplicationChooser`.
  // Thus we need to set `RxdbManager.instance` for every render;
  // this can *not* be set in a `useEffect`.
  if (state.legacy) {
    RxdbManager.instance = RxdbManager.legacyInstance;
  } else {
    RxdbManager.instance = adapter;
  }

  if (state.legacy) {
    return (
      <RxDBHandler>
        <AppV1 />
      </RxDBHandler>
    );
  }

  return (
    <LegacyRxdbManagerHandler manager={adapter}>
      <AppV2 />
    </LegacyRxdbManagerHandler>
  );
};
